import { formatDayTime } from '@/lib/format-day-time';
import {
  CollabStatus,
  type UgcCollabTracking,
  type UgcContentsSummary,
  type UgcMoneyState,
  type UgcNextAction,
  type UgcProductTracking,
} from '@fluenceur/types';
import type { TFunction } from 'i18next';

/**
 * The words of each column of the UGC table, from the projection the server
 * computed. Nothing is decided here: only said.
 */

const K = 'collabs.ugcPage';

/** What names a UGC collab on its page: the brief it was sent from, else its own name. */
export const ugcCollabTitle = (collab: {
  name: string;
  ugc?: Pick<UgcCollabTracking, 'briefName'> | null;
}): string => collab.ugc?.briefName || collab.name;

/** "8 oct.", the day a brand reads in a table cell. */
export const formatShortDay = (iso: string, locale: string): string =>
  new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'short' });

/**
 * A sentence ending on an abbreviated day (« le 22 oct. ») keeps one full stop:
 * the abbreviation's own dot closes it, as French typography wants.
 */
export const closeSentence = (sentence: string): string => sentence.replace(/\.\.$/, '.');

export const formatMinorAmount = (
  minorUnits: number | null,
  currency: string | null,
  locale: string
): string => {
  if (minorUnits === null || !currency) return '';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
  }).format(minorUnits / 100);
};

/** The table's "Next action" line. */
export const nextActionLabel = (t: TFunction, action: UgcNextAction, locale: string): string => {
  const date = action.at ? formatDayTime(action.at, locale) : '';
  switch (action.kind) {
    case 'review_contents':
      // No clock runs (a dispute, the requests spent): no date is promised.
      return action.at
        ? t(`${K}.next.review_contents`, { count: action.count, date })
        : t(`${K}.next.review_contents_undated`, { count: action.count });
    case 'fund':
      // An amount the brand cannot be quoted is never written.
      return action.amountMinorUnits === null || !action.currency
        ? t(`${K}.next.fundUnquoted`, { date })
        : t(`${K}.next.fund`, {
            amount: formatMinorAmount(action.amountMinorUnits, action.currency, locale),
            date,
          });
    case 'shipment_choice':
      return t(`${K}.next.shipment_choice`, {
        date: action.at ? formatShortDay(action.at, locale) : '',
      });
    default:
      return t(`${K}.next.${action.kind}`);
  }
};

/** The table's "Product" line: shipped is never written as delivered. */
export const productLabel = (t: TFunction, product: UgcProductTracking, locale: string): string => {
  switch (product.state) {
    case 'ordered':
      return product.orderName
        ? t(`${K}.product.ordered`, { order: product.orderName })
        : t(`${K}.product.orderPending`);
    case 'shipped':
      return t(`${K}.product.shipped`, {
        date: product.shippedAt ? formatShortDay(product.shippedAt, locale) : '',
      });
    case 'received': {
      const date = product.receivedAt ? formatShortDay(product.receivedAt, locale) : '';
      return product.receptionSource === 'default'
        ? t(`${K}.product.receivedDefault`, { date })
        : t(`${K}.product.received`, { date });
    }
    default:
      return t(`${K}.product.${product.state}`);
  }
};

const ENDED: CollabStatus[] = [
  CollabStatus.DECLINED,
  CollabStatus.EXPIRED,
  CollabStatus.WITHDRAWN,
  CollabStatus.UNFUNDED,
  CollabStatus.CANCELLED,
  CollabStatus.ABANDONED,
];

/** Whether a collab ended without going through: every end reads gray. */
export const isEndedStatus = (status: CollabStatus): boolean => ENDED.includes(status);

/** The table's "Contents" line: "1 of 4 validated · 1 to review". */
export const contentsLabel = (
  t: TFunction,
  contents: UgcContentsSummary,
  status: CollabStatus
): string => {
  if (isEndedStatus(status) && contents.validated === 0) return t(`${K}.contents.notDelivered`);
  const progress = t(`${K}.contents.progress`, {
    validated: contents.validated,
    total: contents.total,
  });
  return contents.toReview > 0
    ? `${progress} · ${t(`${K}.contents.toReview`, { count: contents.toReview })}`
    : progress;
};

/** The short money state of the Compensation column. */
export const moneyLabel = (t: TFunction, ugc: UgcCollabTracking, locale: string): string => {
  if (ugc.moneyState !== 'paid') return t(`${K}.money.${ugc.moneyState}`);
  // A payment whose date is not known yet still reads as paid, never as a raw template.
  return ugc.paidAt
    ? t(`${K}.money.paid`, { date: formatShortDay(ugc.paidAt, locale) })
    : t(`${K}.money.paidUndated`);
};

/** How the payment card reads: amber while the brand has to fund, green once the creator is paid. */
const MONEY_TONE: Record<UgcMoneyState, 'warning' | 'neutral' | 'success'> = {
  no_charge: 'neutral',
  to_fund: 'warning',
  secured: 'neutral',
  validated_payment_pending: 'success',
  paid: 'success',
  refunded_in_full: 'neutral',
  principal_refunded_fees_kept: 'neutral',
};

/** States the card says in its title alone. */
const TITLE_ONLY: ReadonlySet<UgcMoneyState> = new Set(['no_charge', 'paid']);

/**
 * The money in the brand's words, as the payment card states it: a title, the
 * sentence under it, and the card's tone. Read from the server's UGC money
 * state only, the same one the table's Compensation column reads.
 */
export const moneySummary = (
  t: TFunction,
  ugc: UgcCollabTracking,
  amount: string,
  creatorFirstName: string,
  locale: string
): { title: string; text: string | null; tone: 'warning' | 'neutral' | 'success' } => {
  const key = `${K}.drawer.payment.summary.${ugc.moneyState}`;
  const values = {
    amount,
    name: creatorFirstName,
    date: ugc.paidAt ? formatShortDay(ugc.paidAt, locale) : '',
  };
  return {
    title: closeSentence(t(`${key}.title`, values)),
    text: TITLE_ONLY.has(ugc.moneyState) ? null : t(`${key}.text`, values),
    tone: MONEY_TONE[ugc.moneyState],
  };
};

/** « Vidéo 2 », « Photo 1 »: a content by its kind and its own number among those of its kind. */
export const ugcContentLabel = (
  t: (key: string, options?: Record<string, unknown>) => string,
  deliverable: { format: string; unitIndex: number }
): string =>
  t(`${K}.drawer.contents.unit.${deliverable.format === 'ugc_photo' ? 'photo' : 'video'}`, {
    n: deliverable.unitIndex,
  });
